// Banker file-upload helper — mints a signed PUT URL, PUTs the file bytes
// directly to GCS, then resolves with the metadata payload the
// POST /api/banker/artifacts route expects. Used by both the in-line modal
// on banker-task-detail.tsx and the standalone banker-upload-artifact-modal.
//
// We deliberately do not stream bytes through our API server — keeps memory
// flat regardless of file size and means an upload retry doesn't have to
// re-traverse our infra.

const BASE = import.meta.env.BASE_URL;

export interface BankerUploadResult {
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

export async function uploadBankerFile(
  file: File,
  onProgress?: (loaded: number, total: number) => void,
): Promise<BankerUploadResult> {
  const token = localStorage.getItem("banker_token");
  if (!token) throw new Error("Banker session expired — log in again.");

  // Step 1: mint signed PUT URL (server validates auth + MIME + size).
  const signRes = await fetch(`${BASE}api/banker/artifact-upload-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-banker-token": token },
    body: JSON.stringify({ fileName: file.name, mimeType: file.type, fileSize: file.size }),
  });
  if (!signRes.ok) {
    const e = await signRes.json().catch(() => ({}));
    throw new Error(e.error || "Could not get upload URL");
  }
  const { uploadURL } = await signRes.json();
  if (!uploadURL || typeof uploadURL !== "string") {
    throw new Error("Server returned no upload URL");
  }

  // Step 2: PUT bytes directly to GCS via XMLHttpRequest so we can track
  // progress. fetch() doesn't expose upload progress yet.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadURL);
    if (file.type) xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (ev) => { if (ev.lengthComputable && onProgress) onProgress(ev.loaded, ev.total); };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.ontimeout = () => reject(new Error("Upload timed out"));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (HTTP ${xhr.status}). ${xhr.responseText?.slice(0, 200) || ""}`));
    };
    xhr.send(file);
  });

  return { fileUrl: uploadURL, fileName: file.name, fileSize: file.size, mimeType: file.type || "application/octet-stream" };
}
